import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Terminal, ArrowLeft, Send, Rocket, Code2, Eye, Copy, Check,
  Globe, ExternalLink, User, Sparkles,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function Builder() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [building, setBuilding] = useState(false);
  const [tab, setTab] = useState("preview");
  const [copied, setCopied] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const scrollRef = useRef(null);
  const autoTriggered = useRef(false);

  const load = async () => {
    const [p, m] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/projects/${projectId}/messages`),
    ]);
    setProject(p.data);
    setMessages(m.data);
    return { project: p.data, messages: m.data };
  };

  useEffect(() => {
    load()
      .then(({ project, messages }) => {
        const auto = searchParams.get("autobuild");
        if (auto && messages.length === 0 && !autoTriggered.current) {
          autoTriggered.current = true;
          setSearchParams({}, { replace: true });
          runBuild(project.prompt);
        }
      })
      .catch(() => {
        toast.error("Project not found");
        navigate("/");
      });
    // eslint-disable-next-line
  }, [projectId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, building]);

  const runBuild = async (prompt) => {
    if (!prompt || !prompt.trim() || building) return;
    setBuilding(true);
    setMessages((m) => [...m, { role: "user", content: prompt }]);
    try {
      const { data } = await api.post(`/projects/${projectId}/build`, { prompt });
      setProject((p) => ({ ...p, current_html: data.html }));
      setMessages((m) => [...m, { role: "assistant", content: data.summary }]);
      setTab("preview");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Build failed";
      toast.error(msg);
      setMessages((m) => [...m, { role: "assistant", content: "⚠ " + msg }]);
    } finally {
      setBuilding(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const p = input;
    setInput("");
    runBuild(p);
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/publish`);
      setProject((p) => ({ ...p, published: true, published_slug: data.slug }));
      toast.success("Published live!", {
        description: data.url,
        action: { label: "Open", onClick: () => window.open(data.url, "_blank") },
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPublishing(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(project?.current_html || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const publishedUrl = project?.published_slug
    ? `${process.env.REACT_APP_BACKEND_URL}/api/published/${project.published_slug}`
    : null;

  if (!project)
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <span className="font-mono text-sm text-muted-foreground animate-blink">loading...</span>
      </div>
    );

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      {/* Top bar */}
      <header className="h-14 shrink-0 border-b border-border flex items-center justify-between px-4 backdrop-blur-xl bg-background/70">
        <div className="flex items-center gap-3 min-w-0">
          <button
            data-testid="back-button"
            onClick={() => navigate("/")}
            className="h-9 w-9 flex items-center justify-center border border-border rounded-sm hover:bg-muted transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <Terminal className="h-4 w-4 text-primary shrink-0" />
          <span className="font-heading font-semibold truncate">{project.name}</span>
          {project.published && (
            <span className="hidden sm:flex items-center gap-1 font-mono text-xs text-primary border border-primary/40 rounded-full px-2 py-0.5">
              <Globe className="h-3 w-3" /> live
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {publishedUrl && (
            <a
              data-testid="view-live-link"
              href={publishedUrl}
              target="_blank"
              rel="noreferrer"
              className="hidden md:flex h-9 px-3 items-center gap-2 border border-border rounded-sm text-sm hover:bg-muted transition-colors"
            >
              <ExternalLink className="h-4 w-4" /> View live
            </a>
          )}
          <ThemeToggle />
          <button
            data-testid="publish-button"
            onClick={publish}
            disabled={publishing || !project.current_html}
            className="h-9 px-4 bg-accent text-accent-foreground font-mono text-sm uppercase tracking-wider rounded-sm flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            <Rocket className="h-4 w-4" />
            {publishing ? "deploying..." : project.published ? "republish" : "publish"}
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 flex min-h-0">
        {/* Chat pane */}
        <div className="w-full md:w-[380px] shrink-0 border-r border-border flex flex-col min-h-0">
          <div className="px-4 py-3 border-b border-border">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              // build chat
            </p>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && !building && (
              <p className="text-sm text-muted-foreground font-mono">
                Describe your first build below.
              </p>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid="chat-message" className="flex gap-3">
                <div
                  className={`h-7 w-7 shrink-0 flex items-center justify-center border rounded-sm ${
                    m.role === "user" ? "border-border bg-muted" : "border-primary/40 bg-primary/10"
                  }`}
                >
                  {m.role === "user" ? (
                    <User className="h-3.5 w-3.5" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    {m.role === "user" ? "you" : "forge"}
                  </p>
                  <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>
                </div>
              </div>
            ))}
            {building && (
              <div className="flex gap-3">
                <div className="h-7 w-7 shrink-0 flex items-center justify-center border border-primary/40 bg-primary/10 rounded-sm">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    forge
                  </p>
                  <p className="font-mono text-sm text-primary">
                    Compiling<span className="animate-blink">_</span>
                    <br />
                    <span className="text-muted-foreground text-xs">
                      writing code · this can take ~20-40s
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>
          <form onSubmit={submit} className="border-t border-border p-3">
            <div className="border border-border rounded-sm bg-card focus-within:border-primary transition-colors">
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit(e);
                  }
                }}
                placeholder="Ask for a change, e.g. 'add a dark mode toggle'"
                rows={2}
                disabled={building}
                className="w-full bg-transparent p-3 resize-none outline-none text-sm placeholder:text-muted-foreground disabled:opacity-50"
              />
              <div className="flex justify-end p-2 border-t border-border">
                <button
                  data-testid="send-build-button"
                  type="submit"
                  disabled={building || !input.trim()}
                  className="h-8 px-3 bg-primary text-primary-foreground rounded-sm flex items-center gap-1.5 text-sm font-mono hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  <Send className="h-3.5 w-3.5" /> send
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Code / Preview pane */}
        <div className="hidden md:flex flex-1 flex-col min-h-0">
          <div className="h-11 shrink-0 border-b border-border flex items-center px-2 gap-1">
            <TabButton active={tab === "preview"} onClick={() => setTab("preview")} icon={Eye} testid="tab-preview">
              Preview
            </TabButton>
            <TabButton active={tab === "code"} onClick={() => setTab("code")} icon={Code2} testid="tab-code">
              Code
            </TabButton>
            <div className="flex-1" />
            {tab === "code" && project.current_html && (
              <button
                data-testid="copy-code-button"
                onClick={copyCode}
                className="h-8 px-3 flex items-center gap-1.5 text-sm border border-border rounded-sm hover:bg-muted transition-colors"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "copied" : "copy"}
              </button>
            )}
          </div>

          <div className="flex-1 min-h-0 bg-white">
            {!project.current_html ? (
              <div className="h-full flex items-center justify-center bg-background">
                <p className="font-mono text-sm text-muted-foreground">
                  {building ? "compiling your app..." : "no build yet — send a prompt to begin"}
                </p>
              </div>
            ) : tab === "preview" ? (
              <iframe
                data-testid="preview-iframe"
                title="preview"
                srcDoc={project.current_html}
                sandbox="allow-scripts allow-forms allow-modals allow-popups allow-same-origin"
                className="w-full h-full border-0 bg-white"
              />
            ) : (
              <pre
                data-testid="code-view"
                className="h-full overflow-auto p-4 bg-[#0D0D0C] text-[#F7F7F6] font-mono text-xs leading-relaxed"
              >
                {project.current_html}
              </pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`h-8 px-3 flex items-center gap-1.5 text-sm rounded-sm transition-colors ${
        active ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </button>
  );
}
